import { SectionEyebrow, SectionTitle, FadeUp } from "./_shared";

const INPUTS = ["Lab Work", "DNA", "Microbiome", "Wearables", "Biometrics", "Health History"];
const ENGINE_FNS = ["Pattern Recognition", "Multi-Domain Analysis", "Safety Routing", "Evidence Evaluation", "Longitudinal Context"];

const FlowArrow = () => <div className="flex justify-center py-2" aria-hidden="true"><span className="text-accent text-xl font-bold leading-none">↓</span></div>;
const StageLabel = ({ children }) => <div className="font-mono text-[0.6rem] uppercase tracking-[0.24em] text-muted-foreground text-center mb-2">{children}</div>;

const DataStage = () => (
  <div className="border border-border bg-card p-4 md:p-5">
    <StageLabel>Data Inputs</StageLabel>
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      {INPUTS.map((label) => <div key={label} className="border border-border bg-background py-2 px-2 text-center font-mono text-[0.62rem] md:text-[0.68rem] uppercase tracking-[0.12em] text-primary font-semibold">{label}</div>)}
    </div>
  </div>
);

const EngineStage = () => (
  <div className="border-2 border-accent bg-card p-4 md:p-5 relative">
    <div className="text-center font-display font-bold text-primary text-base md:text-lg tracking-tight">INTEGRATED MATRIX ENGINE</div>
    <div className="text-center font-mono text-[0.55rem] uppercase tracking-[0.2em] text-accent mt-1 mb-3">Patent Pending</div>
    <div className="flex flex-wrap justify-center gap-2">
      {ENGINE_FNS.map((item) => <span key={item} className="border border-accent/40 bg-accent/5 py-1.5 px-3 font-mono text-[0.6rem] uppercase tracking-[0.12em] text-foreground/80">{item}</span>)}
    </div>
  </div>
);

function SimpleStage({ title, subtitle, primary = false }) {
  return (
    <div className={`${primary ? "border-2 border-primary bg-primary text-primary-foreground" : "border border-border bg-card text-primary"} text-center py-4`}>
      <span className="font-display font-bold text-base md:text-lg tracking-tight">{title}</span>
      {subtitle && <div className="font-mono text-[0.58rem] uppercase tracking-[0.18em] text-muted-foreground mt-1">{subtitle}</div>}
    </div>
  );
}

const LoopStage = () => <div className="border-2 border-primary/30 bg-primary/5 text-center py-3.5"><span className="font-mono text-[0.68rem] md:text-[0.75rem] uppercase tracking-[0.24em] text-primary font-semibold">Measure → Learn → Adjust</span></div>;

export function MatrixFlowDiagram({ compact = false }) {
  return (
    <div data-testid="matrix-flow-diagram" className={`max-w-2xl mx-auto ${compact ? "" : "md:max-w-3xl"}`}>
      <SimpleStage title="PATIENT" primary /><FlowArrow />
      <DataStage /><FlowArrow />
      <EngineStage /><FlowArrow />
      <SimpleStage title="CLINICIAN REVIEW" subtitle="Licensed medical professionals" /><FlowArrow />
      <SimpleStage title="PERSONALIZED PROTOCOL" /><FlowArrow />
      <LoopStage />
    </div>
  );
}

export default function MatrixFlowSection() {
  return (
    <section id="how-it-works" data-testid="matrix-flow-section" className="py-24 md:py-32 bg-card/40 border-y border-border">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <FadeUp className="text-center max-w-3xl mx-auto">
          <SectionEyebrow>How It Works</SectionEyebrow>
          <SectionTitle className="mt-4 text-[clamp(1.9rem,4.5vw,3.25rem)]">How <em className="not-italic text-accent">Integrated Wellness</em> works.</SectionTitle>
          <p className="mt-6 text-lg md:text-xl text-foreground/75 leading-relaxed">Your data → the Integrated Matrix Engine → clinician review → your protocol. One pipeline, measured and adjusted over time.</p>
        </FadeUp>
        <FadeUp delay={0.12} className="mt-14"><MatrixFlowDiagram /></FadeUp>
        <FadeUp delay={0.15} className="mt-12 text-center">
          <a href="/digital-twin" data-testid="how-it-works-digital-twin-link" className="inline-flex items-center gap-2 font-mono text-[0.7rem] uppercase tracking-[0.22em] text-primary hover:text-accent font-semibold transition-colors">Learn how your Digital Twin works <span aria-hidden>→</span></a>
        </FadeUp>
      </div>
    </section>
  );
}